import {Box, Show} from 'platform/foundation';
import styled from 'styled-components';

import {useRef, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {MAX_EMAIL_WIDTH} from '../constants/MAX_EMAIL_WIDTH';
import {ThreadEmailSize} from '../types/ThreadEmailSize';
import {buildThreadEmailDocument} from '../utils/buildThreadEmailDocument';

const MEASURING_HEIGHT = 600;

interface ThreadEmailWindowProps extends TestIdProps {
  htmlData: string | null;
  onMeasure?: (size: ThreadEmailSize) => void;
}

// Thread-only copy of EmailWindow. Unlike the shared EmailWindow (sandbox=""), this uses
// sandbox="allow-same-origin" (still NO allow-scripts — the email stays inert and DOMPurify strips
// scripts) so the parent can read the rendered document and size the iframe to it. allow-popups
// (+ allow-popups-to-escape-sandbox) is what lets the target="_blank" links set by
// buildThreadEmailDocument open a normal, unsandboxed tab on click — without it the sandboxed frame
// can neither open a window nor load the site in place, so a click does nothing. An iframe never
// auto-grows to its document, so we measure the content and drive the iframe height from it; the
// card then grows to the iframe. We measure the email's natural width (max-content) to size the
// card, then switch the wrapper to fill the window so designed templates (width:100% tables) expand
// to the full width instead of sitting in a narrow column. The content is never scaled/zoomed.
export function ThreadEmailWindow(props: ThreadEmailWindowProps) {
  const ref = useRef<HTMLIFrameElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [size, setSize] = useState<ThreadEmailSize | null>(null);

  const handleLoad = () => {
    const fit = ref.current?.contentDocument?.getElementById('omnetic-fit');
    if (isNil(fit)) {
      return;
    }
    // Natural (max-content) width sizes the card; capture it before the wrapper fills the window.
    const width = Math.min(fit.offsetWidth, MAX_EMAIL_WIDTH);
    // Fill the window and switch to a flow-root box. `flow-root` (vs the inline-block used for
    // measuring) both drops the inline baseline gap AND establishes a block formatting context, so
    // the children's vertical margins are contained inside the element. Without a BFC a leading or
    // trailing <p>/<div> margin collapses out of #omnetic-fit and the measured height comes up short,
    // leaving a stray scrollbar. Height is rounded up so a fractional height never overflows either.
    fit.style.display = 'flow-root';
    fit.style.width = '100%';
    fit.style.maxWidth = 'none';

    // The card width can change AFTER the natural width is reported (the min/max % clamps, a panel
    // resize), which reflows the email to a new height. Keep the iframe height matched to the
    // content's real height so the iframe never needs its own scrollbar and the card grows with it.
    const syncHeight = () =>
      setSize({width, height: Math.ceil(fit.getBoundingClientRect().height)});

    syncHeight();
    props.onMeasure?.({width, height: Math.ceil(fit.getBoundingClientRect().height)});

    observerRef.current?.disconnect();
    observerRef.current = new ResizeObserver(syncHeight);
    observerRef.current.observe(fit);
  };

  // Callback ref so the observer is torn down when the iframe unmounts (no useEffect needed).
  const setIframeRef = (node: HTMLIFrameElement | null) => {
    ref.current = node;
    if (isNil(node)) {
      observerRef.current?.disconnect();
      observerRef.current = null;
    }
  };

  return (
    <Box width="100%" data-testid={props['data-testid']}>
      <Show when={isNotNil(props.htmlData)}>
        <IFrame
          ref={setIframeRef}
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          srcDoc={buildThreadEmailDocument(props.htmlData, i18n.t('general.labels.showQuotedText'))}
          onLoad={handleLoad}
          loading="eager"
          $height={size?.height ?? MEASURING_HEIGHT}
          $isLoaded={isNotNil(size)}
          data-testid={suffixTestId('iframe', props)}
        />
      </Show>
    </Box>
  );
}

// eslint-disable-next-line eag/no-css-property
const IFrame = styled.iframe<{$isLoaded: boolean; $height: number}>`
  display: block;
  width: 100%;
  height: ${({$height}) => $height}px;
  border: 0;
  overflow: auto;
  background-color: #fff;
  border-radius: 8px;
  visibility: ${({$isLoaded}) => ($isLoaded ? 'visible' : 'hidden')};
`;
